import { Injectable, inject } from '@angular/core';
import { MembershipPortalApiService, MembershipPortalMe } from '@api/membership';
import { SessionService } from '@core/services';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { HelperService } from '../../helper.service';

/**
 * Who this partner is, and whether Lifemaan has switched them on — loaded once, read everywhere.
 *
 * ⚠ `GET /portal/me` IS THE ONLY PORTAL ENDPOINT THAT ANSWERS AN UNAPPROVED PARTNER. Every other
 * one is behind `assertParticipating` and refuses outright, which is what makes self-service
 * sign-up safe (D-021). So this is the first call the portal makes and the one the shell's guard
 * waits on; asking anything else first gets a refusal that reads like a broken product.
 *
 * ⚠ THE THREE STATES ARE NOT TWO. `isParticipating` alone cannot tell a partner who applied and is
 * waiting from a hospital that never applied, and telling the second "we are reviewing your
 * application" is a promise about something nobody is doing. `awaitingReview` is what separates
 * them, and it comes from the server rather than being inferred here.
 *
 * ⚠ A FAILED LOAD IS NOT CACHED as a permanent "not a partner". `shareReplay` sits on the raw
 * request and the recovery is downstream, so a blip costs one retry rather than the rest of the
 * session — the "empty-for-ever" trap the working agreement records against the `shareReplay`
 * recipe, where a one-off failure is mapped to a benign value and then served for ever.
 */
@Injectable()
export class PartnerPortalStateService {
    private readonly portalApi = inject(MembershipPortalApiService);
    private readonly helper = inject(HelperService);

    private readonly meSubject$ = new BehaviorSubject<MembershipPortalMe | null>(null);

    /** The partner's own record, or null while it is unknown. */
    readonly me$: Observable<MembershipPortalMe | null> = this.meSubject$.asObservable();

    private request$: Observable<MembershipPortalMe | null> | null = null;

    /*
     * WHOSE ANSWER IS CACHED (owner, 2026-10-21: a doctor signed in after the hospital owner, in the same tab, saw the owner's
     * full menu and was refused on Redemptions). The router never destroys the `/partner` route's injector (this app does not opt in to injector clean-up), so this service outlives a
     * sign-out and an entity switch. The cached `me` is therefore tied to the person and the hospital it was read for, and is
     * read again whenever either changes.
     */
    private cachedFor = '';

    constructor() {
        const sessionService = inject(SessionService);

        // A signed-out session's answer must never be shown to whoever signs in next. Lives as long as the service.
        sessionService.sessionEnded$.subscribe(() => this.clear());
    }

    /** The person and hospital the portal is acting for right now. */
    private currentKey(): string {
        return `${this.helper.getUserId() || ''}|${this.helper.getLocalStorage('hospitalId') || ''}`;
    }

    /** The last answer, synchronously. Null until `ensureLoaded$` has resolved at least once. */
    get snapshot(): MembershipPortalMe | null {
        return this.meSubject$.value;
    }

    /**
     * Load once; every later caller gets the same answer without a second call.
     *
     * Resolves to `null` when the partner cannot be identified at all — a failed request, a
     * `success: false` envelope, or a refusal. The guard reads that as "cannot show the portal"
     * rather than as "not approved", because the two need different words on screen.
     */
    ensureLoaded$(): Observable<MembershipPortalMe | null> {
        const key = this.currentKey();
        if (key !== this.cachedFor) {
            this.clear();
            this.cachedFor = key;
        }
        if (this.meSubject$.value) { return of(this.meSubject$.value); }
        if (this.request$) { return this.request$; }

        const request$ = this.portalApi.getMe().pipe(
            map((response) => (response.success && response.data ? response.data : null)),
            tap((me) => {
                /*
                 * ⚠ AN ANSWER FOR A SESSION THAT HAS SINCE ENDED IS DROPPED (review MR !1 R3). A request still in flight at
                 * sign-out would otherwise land after `clear()` and put the previous partner back in the cache.
                 */
                if (this.currentKey() !== key) { return; }
                this.meSubject$.next(me);
                // A resolved answer is kept on the subject, so the shared request can be released.
                // Keeping it would make a later `reload$` share a stale completed observable.
                if (this.request$ === request$) { this.request$ = null; }
            }),
            map((me) => (this.currentKey() === key ? me : null)),
            catchError(() => {
                if (this.request$ === request$) { this.request$ = null; }
                return of(null);
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        this.request$ = request$;

        return request$;
    }

    /**
     * Re-read after something that can change the answer — a redemption (totals move) or a partner
     * checking whether their application has been approved yet.
     */
    reload$(): Observable<MembershipPortalMe | null> {
        this.meSubject$.next(null);
        this.request$ = null;
        return this.ensureLoaded$();
    }

    /** Cleared on sign-out so the next account does not read the previous one's totals. */
    clear(): void {
        this.meSubject$.next(null);
        this.request$ = null;
        this.cachedFor = '';
    }
}
